import { mergeAttributes, Node } from "@tiptap/core";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    diffDeletion: {
      /**
       * Insert a diff deletion node at a specific position.
       * Used to display text that was removed (existed in original but not in current).
       */
      insertDiffDeletion: (text: string, position: number) => ReturnType;
      /**
       * Remove all diff deletion nodes from the document.
       */
      removeAllDiffDeletions: () => ReturnType;
    };
  }
}

/**
 * TipTap inline Node extension for displaying deleted text in diff mode.
 *
 * Since deleted text doesn't exist in the current content, we must INSERT it
 * as an inline node that renders with strikethrough styling.
 *
 * Key features:
 * - Inline node that displays deleted text with rose-500 color and strikethrough
 * - atom: true prevents cursor from entering the node
 * - Does NOT serialize to markdown (ephemeral visual state only)
 * - Can coexist with other marks and nodes
 *
 * Usage:
 * ```typescript
 * editor.commands.insertDiffDeletion("removed text", 10);
 * ```
 */
export const DiffDeletionNode = Node.create({
  name: "diffDeletion",

  group: "inline",

  inline: true,

  // Atomic node - cursor cannot be placed inside
  atom: true,

  // Mark this as non-selectable to avoid confusion
  selectable: false,

  // Don't allow dragging
  draggable: false,

  addAttributes() {
    return {
      text: {
        default: "",
        parseHTML: (element: HTMLElement) => element.textContent || "",
        // Don't render as HTML attribute - text is rendered as content
        renderHTML: () => ({}),
      },
    };
  },

  // No parseHTML - these nodes are created only via insertDiffDeletion command.
  // Omitting parseHTML prevents pasted HTML with diff-deletion markup from
  // recreating the styled node; the text content will be pasted as plain text instead.
  parseHTML() {
    return [];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        class: "diff-deletion",
        contenteditable: "false",
        "data-diff-deletion": "true",
      }),
      node.attrs.text,
    ];
  },

  // When copied, serialize as plain text so pasting doesn't carry the node styling
  renderText({ node }) {
    return String(node.attrs.text);
  },

  addCommands() {
    return {
      insertDiffDeletion:
        (text: string, position: number) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            const node = this.type.create({ text });
            tr.insert(position, node);
          }
          return true;
        },

      removeAllDiffDeletions:
        () =>
        ({ tr, state, dispatch }) => {
          if (!dispatch) return false;

          const { doc } = state;
          const nodesToRemove: { pos: number; size: number }[] = [];

          // Find all diff deletion nodes
          doc.descendants((node, pos) => {
            if (node.type.name === this.name) {
              nodesToRemove.push({ pos, size: node.nodeSize });
            }
          });

          // Remove in reverse order to preserve positions
          nodesToRemove.reverse().forEach(({ pos, size }) => {
            tr.delete(pos, pos + size);
          });

          return true;
        },
    };
  },
});
